import React, { useState, useEffect } from 'react';
import { X, UserCheck, Loader2, AlertCircle } from 'lucide-react';
import api from '../api/axios';
import { useToast } from './Toast';

const timeToMinutes = (t) => {
  if (!t || !t.includes('h')) return 0;
  const [h, m] = t.split('h').map(Number);
  return h * 60 + (m || 0);
};

export default function BookingAssignModal({ booking, employees, onClose, onAssigned }) {
  const showToast = useToast();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyEmployeeIds, setBusyEmployeeIds] = useState(new Set());
  const [loadingAvail, setLoadingAvail] = useState(true);

  // Fetch bookings for the same date to detect conflicts
  useEffect(() => {
    const fetchConflicts = async () => {
      try {
        const dateStr = booking.date
          ? new Date(booking.date).toISOString().split('T')[0]
          : null;
        if (!dateStr) return;

        const res = await api.get(`/admin/bookings/${booking.salonId}`, {
          params: { startDate: dateStr, endDate: dateStr }
        });
        const dayBookings = (res.data?.data || []).filter(
          (b) => b._id !== booking._id && b.status !== 'Annulé' && b.employeeId
        );

        const slotStart = timeToMinutes(booking.heureDebut);
        const slotEnd   = timeToMinutes(booking.heureFin);

        const busy = new Set();
        dayBookings.forEach((b) => {
          const bStart = timeToMinutes(b.heureDebut);
          const bEnd   = timeToMinutes(b.heureFin);
          if (slotStart < bEnd && slotEnd > bStart) {
            busy.add(String(b.employeeId?._id || b.employeeId));
          }
        });
        setBusyEmployeeIds(busy);
      } catch {
        // En cas d'erreur on affiche tous les employés sans filtrer
      } finally {
        setLoadingAvail(false);
      }
    };
    fetchConflicts();
  }, [booking]);

  const handleAssign = async () => {
    if (!selectedEmployeeId) return;
    setLoading(true);
    try {
      await api.put(`/admin/bookings/${booking._id}/assign`, { employeeId: selectedEmployeeId });
      showToast('Employé assigné avec succès !');
      onAssigned();
    } catch (err) {
      showToast(err.response?.data?.message || "Erreur lors de l'assignation", 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const availableEmployees = employees.filter((e) => !busyEmployeeIds.has(String(e._id)));
  const unavailableEmployees = employees.filter((e) => busyEmployeeIds.has(String(e._id)));

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-md shadow-2xl relative max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="p-6 pb-4 shrink-0">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 transition-colors p-1"
          >
            <X size={22} />
          </button>
          <h2 className="text-xl font-black text-slate-900">Assigner un employé</h2>
          <p className="text-slate-400 text-sm mt-1">Seuls les employés disponibles à ce créneau sont sélectionnables.</p>
        </div>

        {/* Booking summary */}
        <div className="mx-6 mb-4 bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-2 shrink-0">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Rendez-vous</p>
          <Row label="Client"  value={`${booking.clientPrenom || ''} ${booking.clientNom || ''}`.trim() || '-'} />
          <Row label="Date"    value={formatDate(booking.date)} />
          {booking.heureDebut && (
            <Row label="Horaire" value={`${booking.heureDebut}${booking.heureFin ? ` → ${booking.heureFin}` : ''}`} />
          )}
          {booking.service && <Row label="Service" value={booking.service} />}
        </div>

        {/* Employee list */}
        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-2">
          {loadingAvail ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="text-blue-500 animate-spin" />
            </div>
          ) : employees.length === 0 ? (
            <p className="text-slate-400 italic text-sm text-center py-6">Aucun employé disponible.</p>
          ) : (
            <>
              {/* Available */}
              {availableEmployees.length > 0 && (
                <>
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                    Disponibles ({availableEmployees.length})
                  </p>
                  {availableEmployees.map((emp) => {
                    const isSelected = selectedEmployeeId === emp._id;
                    return (
                      <button
                        key={emp._id}
                        onClick={() => setSelectedEmployeeId(emp._id)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-slate-100 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0 ${
                          isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {(emp.prenom?.[0] || '') + (emp.nom?.[0] || '')}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-slate-800 text-sm">{emp.prenom} {emp.nom}</p>
                          {emp.specialite && (
                            <p className="text-xs text-slate-400 font-medium">{emp.specialite}</p>
                          )}
                        </div>
                        {isSelected && <UserCheck size={18} className="text-blue-600 shrink-0" />}
                      </button>
                    );
                  })}
                </>
              )}

              {/* Unavailable */}
              {unavailableEmployees.length > 0 && (
                <>
                  <p className="text-xs font-black text-red-300 uppercase tracking-widest mt-4 mb-2 flex items-center gap-1">
                    <AlertCircle size={12} /> Déjà occupés ({unavailableEmployees.length})
                  </p>
                  {unavailableEmployees.map((emp) => (
                    <div
                      key={emp._id}
                      className="flex items-center gap-4 p-4 rounded-2xl border-2 border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                    >
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-black text-slate-400 shrink-0">
                        {(emp.prenom?.[0] || '') + (emp.nom?.[0] || '')}
                      </div>
                      <div>
                        <p className="font-bold text-slate-400 text-sm">{emp.prenom} {emp.nom}</p>
                        <p className="text-xs text-red-300 font-medium">Occupé à ce créneau</p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {availableEmployees.length === 0 && (
                <div className="flex items-center gap-2 p-4 bg-orange-50 border border-orange-100 rounded-2xl text-orange-700 text-sm font-bold mt-2">
                  <AlertCircle size={18} className="shrink-0" />
                  Tous les employés sont déjà occupés à ce créneau.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-slate-50 shrink-0">
          <button
            onClick={handleAssign}
            disabled={!selectedEmployeeId || loading}
            className="w-full p-4 bg-blue-600 text-white rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-40 disabled:pointer-events-none shadow-lg shadow-blue-100"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <UserCheck size={18} />}
            Assigner
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500 font-medium">{label}</span>
      <span className="font-bold text-slate-800">{value}</span>
    </div>
  );
}
